import React from 'react';
import { Table } from '@tanstack/react-table';
import { TablePagination } from '@mui/material';
import { PageSizeOption } from './types';

interface TanstackTablePaginationProps<TData> {
  table: Table<TData>;
  pageSizeOptions: PageSizeOption[];
  rowCount?: number;
  onPageSizePicked?: (pageSize: number) => void;
}

function optionValue(option: PageSizeOption): number {
  return typeof option === 'number' ? option : option.value;
}

// MUI errors on an unlisted rowsPerPage or out-of-range page; hence the guards.
function TanstackTablePagination<TData>({
  table,
  pageSizeOptions,
  rowCount,
  onPageSizePicked,
}: TanstackTablePaginationProps<TData>): JSX.Element {
  const { pageIndex, pageSize } = table.getState().pagination;
  const count = rowCount ?? table.getFilteredRowModel().rows.length;

  const safeOptions = pageSizeOptions.some(
    (option: PageSizeOption) => optionValue(option) === pageSize
  )
    ? pageSizeOptions
    : [...pageSizeOptions, pageSize].sort(
        (a: PageSizeOption, b: PageSizeOption) => optionValue(a) - optionValue(b)
      );

  const lastPageIndex = Math.max(0, Math.ceil(count / Math.max(1, pageSize)) - 1);
  const safePageIndex = Math.min(pageIndex, lastPageIndex);

  return (
    <TablePagination
      component="div"
      className="max-sm:[&_.MuiTablePagination-selectLabel]:hidden"
      count={count}
      page={safePageIndex}
      rowsPerPage={pageSize}
      rowsPerPageOptions={safeOptions}
      onPageChange={(_: unknown, newPage: number): void => table.setPageIndex(newPage)}
      onRowsPerPageChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
        const nextPageSize = Number(e.target.value);
        table.setPageSize(nextPageSize);
        onPageSizePicked?.(nextPageSize);
      }}
    />
  );
}

export default TanstackTablePagination;
